import {
  Component, Input, Output, EventEmitter, OnChanges, SimpleChanges,
  inject, OnDestroy, HostListener, ViewChild, ElementRef, AfterViewInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { Project, ProjectStage, GanttStageView } from '../../../../shared/models/project.model';
import { Task } from '../../../../shared/models/task.model';
import { User as UserModel } from '../../../../shared/models/user.model';
import { TaskService } from '../../../../shared/services/task.service';
import { ConfirmModalComponent } from '../../../../shared/utils/modal-validation/modal-validation';

type ZoomLevel = 'day' | 'week' | 'month' | 'quarter';
type GanttItem = GanttStageView & { isProject?: boolean; parent?: { color?: string }; realId: string };
interface Row { item: GanttItem; level: number; }
interface AxisSeg { label: string; left: number; width: number; key: string; }
interface DragState {
  item: GanttItem;
  mode: 'move' | 'resize-start' | 'resize-end';
  startClientX: number;
  origStart: Date;
  origEnd: Date;
  previewStart: Date;
  previewEnd: Date;
  moved: boolean;
}

@Component({
  selector: 'app-gantt-stage-project',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, ConfirmModalComponent],
  templateUrl: 'gantt-stage-project.html',
  styleUrl: 'gantt-stage-project.scss',
})
export class GanttStageProjectComponent implements OnChanges, OnDestroy, AfterViewInit {
  @Input() projects: Project[] = [];
  @Input() projectStages: ProjectStage[] = [];
  @Input() employees: UserModel[] = [];
  @Input() encodedHoursByStage: Record<string, number> = {};

  @Output() addTask = new EventEmitter<string>();
  /** The parent owns the projectId, so it performs the navigation to the task page. */
  @Output() taskDetailOpen = new EventEmitter<string>();
  @Output() addStage = new EventEmitter<string>();
  @Output() stageChange = new EventEmitter<{ stageId: string, changes: Partial<ProjectStage> }>();
  @Output() projectChange = new EventEmitter<{ projectId: string, changes: any }>();

  @ViewChild('treeScroll') treeScroll?: ElementRef<HTMLDivElement>;
  @ViewChild('timeScroll') timeScroll?: ElementRef<HTMLDivElement>;

  private taskService = inject(TaskService);

  private tasksSubscription?: Subscription;
  private allTasks: Task[] = [];

  ganttData: GanttItem[] = [];

  // Cached view-model (rebuilt only when data / zoom / expand / dates change,
  // NOT on every change-detection cycle).
  rows: Row[] = [];
  topSegments: AxisSeg[] = [];
  bottomSegments: AxisSeg[] = [];
  timelineWidth = 320;
  chartHeight = 200;
  todayLeft = 0;
  todayVisible = false;

  /** Signature of the current project's tasks — skips rebuilds on unrelated task churn. */
  private lastTaskSig = '';

  taskToDelete: Task | null = null;
  showConfirmation = false;

  /** Inline edit target (name / hours). */
  editing: { id: string; field: 'name' | 'hours' } | null = null;

  // ── Timeline geometry ──────────────────────────────────────────────────────
  readonly ROW_HEIGHT = 44;
  readonly HEADER_HEIGHT = 56;
  zoom: ZoomLevel = 'week';
  private readonly pxPerDayMap: Record<ZoomLevel, number> = { day: 40, week: 16, month: 5, quarter: 2.2 };

  rangeStart = this.midnight(new Date());
  totalDays = 30;

  dragState: DragState | null = null;

  statusOptions = [
    { text: 'New', value: 'NOTBEGIN' },
    { text: 'In Progress', value: 'INPROGRESS' },
    { text: 'Done', value: 'DONE' },
  ];

  private scrollSyncing = false;
  private pendingScrollToToday = false;

  constructor() {
    this.tasksSubscription = this.taskService.tasks$.subscribe(tasks => {
      this.allTasks = tasks;
      // Only rebuild if a task belonging to THIS project actually changed.
      const sig = this.computeTaskSig();
      if (sig !== this.lastTaskSig) this.refreshData();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectStages'] || changes['projects'] || changes['encodedHoursByStage']) {
      this.refreshData();
    }
  }

  /** Cheap fingerprint of the tasks whose stage belongs to the loaded stages. */
  private computeTaskSig(): string {
    const stageIds = new Set(this.projectStages.map(s => s.projectStageId));
    return this.allTasks
      .filter(t => stageIds.has(t.projectStageId))
      .map(t => `${t.id}:${t.status}:${t.startDate}:${t.limitDateToDo}:${t.estimatedHours}:${t.nameTask}`)
      .sort()
      .join('|');
  }

  ngAfterViewInit(): void {
    this.scrollToToday();
  }

  ngOnDestroy(): void {
    this.tasksSubscription?.unsubscribe();
  }

  get pxPerDay(): number { return this.pxPerDayMap[this.zoom]; }

  setZoom(z: ZoomLevel): void {
    if (this.zoom === z) return;
    this.zoom = z;
    this.buildAxis(); // pixel geometry changes; rows are unaffected
    this.pendingScrollToToday = true;
    setTimeout(() => { if (this.pendingScrollToToday) { this.pendingScrollToToday = false; this.scrollToToday(); } });
  }

  // ── Data mapping ─────────────────────────────────────────────────────────────
  private refreshData(): void {
    if (this.projects && this.projects.length > 0) this.mapProjectsToGantt();
    else if (this.projectStages && this.projectStages.length > 0) this.mapStagesToGantt();
    else this.ganttData = [];
    this.lastTaskSig = this.computeTaskSig();
    this.rebuild();
  }

  /** Full rebuild of the cached view-model. Called on data / date changes only. */
  private rebuild(): void {
    this.computeRange();
    this.buildRows();
    this.buildAxis();
  }

  /** Flattened visible rows + chart height (cheap; depends on expand state). */
  private buildRows(): void {
    const rows: Row[] = [];
    const walk = (items: GanttItem[], level: number) => {
      for (const item of items) {
        rows.push({ item, level });
        if (item.expanded && item.subtasks && item.subtasks.length) {
          walk(item.subtasks as GanttItem[], level + 1);
        }
      }
    };
    walk(this.ganttData, 0);
    this.rows = rows;
    this.chartHeight = Math.min(600, this.HEADER_HEIGHT + rows.length * this.ROW_HEIGHT + 2);
  }

  /** Timeline width, axis segments and the today marker (depends on zoom + range). */
  private buildAxis(): void {
    this.timelineWidth = Math.max(this.totalDays * this.pxPerDay, 320);
    if (this.zoom === 'day' || this.zoom === 'week') {
      this.topSegments = this.genSegments('month', d => `${GanttStageProjectComponent.MONTHS[d.getMonth()]} ${d.getFullYear()}`);
    } else {
      this.topSegments = this.genSegments('year', d => `${d.getFullYear()}`);
    }
    switch (this.zoom) {
      case 'day': this.bottomSegments = this.genSegments('day', d => `${d.getDate()}`); break;
      case 'week': this.bottomSegments = this.genSegments('week', d => `${d.getDate()} ${GanttStageProjectComponent.MONTHS[d.getMonth()]}`); break;
      case 'month': this.bottomSegments = this.genSegments('month', d => GanttStageProjectComponent.MONTHS[d.getMonth()]); break;
      case 'quarter': this.bottomSegments = this.genSegments('quarter', d => `Q${Math.floor(d.getMonth() / 3) + 1}`); break;
    }
    const today = this.midnight(new Date());
    this.todayVisible = today >= this.rangeStart && this.daysBetween(this.rangeStart, today) <= this.totalDays;
    this.todayLeft = this.offsetPx(today);
  }

  private mapStagesToGantt(): void {
    this.ganttData = this.projectStages.map(stage => this.createStageGanttItem(stage));
  }

  private mapProjectsToGantt(): void {
    this.ganttData = this.projects.map(project => {
      const projectStages = this.projectStages.filter(s => s.projectId === project.projectId);
      const mappedStages = projectStages.map(stage => this.createStageGanttItem(stage));
      const startTimes = mappedStages.map(s => s.start.getTime()).filter(t => t > 0);
      const endTimes = mappedStages.map(s => s.end.getTime()).filter(t => t > 0);
      const projectStart = startTimes.length > 0 ? new Date(Math.min(...startTimes)) : new Date();
      const projectEnd = endTimes.length > 0 ? new Date(Math.max(...endTimes)) : new Date();
      return {
        id: `proj_${project.projectId}`,
        realId: project.projectId!,
        title: project.projectName,
        start: projectStart,
        end: projectEnd,
        completionRatio: 0,
        status: 'NOTBEGIN',
        isStage: false,
        isProject: true,
        expanded: true,
        estimatedHours: project.estimatedHours || 0,
        subtasks: mappedStages,
        color: (project as any).color || '#3b82f6',
      } as GanttItem;
    });
  }

  private createStageGanttItem(stage: ProjectStage): GanttItem {
    const relatedTasks = this.allTasks.filter(t => t.projectStageId === stage.projectStageId);
    const mappedTasks = relatedTasks.map(task => {
      let taskStart = new Date(task.startDate || stage.startDate);
      let taskEnd = new Date(task.limitDateToDo || stage.endDate);
      if (taskEnd < taskStart) taskEnd = taskStart;
      return {
        id: `task_${task.id}`,
        realId: task.id!,
        title: task.nameTask,
        start: taskStart,
        end: taskEnd,
        completionRatio: task.status === 'DONE' ? 1 : (task.status === 'INPROGRESS' ? 0.5 : 0),
        status: task.status || 'NOTBEGIN',
        estimatedHours: task.estimatedHours || 0,
        isStage: false,
        isProject: false,
        subtasks: [],
        parent: { color: stage.color },
      } as GanttItem;
    });
    return {
      id: `stage_${stage.projectStageId}`,
      realId: stage.projectStageId!,
      title: stage.stageName,
      start: new Date(stage.startDate),
      end: new Date(stage.endDate),
      completionRatio: stage.status === 'DONE' ? 1 : (stage.status === 'INPROGRESS' ? 0.5 : 0),
      status: stage.status || 'NOTBEGIN',
      estimatedHours: stage.estimatedHours || 0,
      encodedHours: this.encodedHoursByStage[stage.projectStageId!] || 0,
      isStage: true,
      isProject: false,
      expanded: true,
      subtasks: mappedTasks,
      color: stage.color,
    } as GanttItem;
  }

  toggleExpand(item: GanttItem): void {
    item.expanded = !item.expanded;
    this.buildRows(); // visible row set changed; axis/range unaffected
  }

  hasChildren(item: GanttItem): boolean {
    return !!(item.subtasks && item.subtasks.length);
  }

  // ── Date helpers ─────────────────────────────────────────────────────────────
  private midnight(d: Date): Date { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
  private addDays(d: Date, n: number): Date { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
  private daysBetween(a: Date, b: Date): number {
    return Math.round((this.midnight(b).getTime() - this.midnight(a).getTime()) / 86400000);
  }

  private collectDates(items: GanttItem[], acc: number[]): void {
    for (const it of items) {
      const s = it.start?.getTime?.(); const e = it.end?.getTime?.();
      if (s && !isNaN(s)) acc.push(s);
      if (e && !isNaN(e)) acc.push(e);
      if (it.subtasks && it.subtasks.length) this.collectDates(it.subtasks as GanttItem[], acc);
    }
  }

  private computeRange(): void {
    const times: number[] = [];
    this.collectDates(this.ganttData, times);
    const today = this.midnight(new Date());
    let min: Date, max: Date;
    if (times.length === 0) {
      min = this.addDays(today, -14);
      max = this.addDays(today, 45);
    } else {
      min = this.midnight(new Date(Math.min(...times)));
      max = this.midnight(new Date(Math.max(...times)));
      // Always keep today in view
      if (today < min) min = today;
      if (today > max) max = today;
      min = this.addDays(min, -7);
      max = this.addDays(max, 14);
    }
    this.rangeStart = min;
    this.totalDays = Math.max(this.daysBetween(min, max) + 1, 30);
  }

  private offsetPx(d: Date): number {
    const days = Math.min(Math.max(this.daysBetween(this.rangeStart, d), 0), this.totalDays);
    return days * this.pxPerDay;
  }

  // ── Bars ──────────────────────────────────────────────────────────────────────
  private barDates(item: GanttItem): { start: Date; end: Date } {
    if (this.dragState && this.dragState.item === item) {
      return { start: this.dragState.previewStart, end: this.dragState.previewEnd };
    }
    return { start: item.start, end: item.end };
  }

  barLeft(item: GanttItem): number {
    return this.offsetPx(this.barDates(item).start);
  }

  barWidth(item: GanttItem): number {
    const { start, end } = this.barDates(item);
    const days = this.daysBetween(start, end) + 1;
    return Math.max(days * this.pxPerDay, 10);
  }

  // ── Axis segments ───────────────────────────────────────────────────────────
  private startOfWeek(d: Date): Date {
    const x = this.midnight(d);
    const day = (x.getDay() + 6) % 7; // Monday = 0
    return this.addDays(x, -day);
  }

  private genSegments(unit: 'day' | 'week' | 'month' | 'quarter' | 'year', label: (d: Date) => string): AxisSeg[] {
    const segs: AxisSeg[] = [];
    const rangeEndExclusive = this.totalDays; // in days from rangeStart
    let cursor: Date;
    switch (unit) {
      case 'day': cursor = this.midnight(this.rangeStart); break;
      case 'week': cursor = this.startOfWeek(this.rangeStart); break;
      case 'month': cursor = new Date(this.rangeStart.getFullYear(), this.rangeStart.getMonth(), 1); break;
      case 'quarter': cursor = new Date(this.rangeStart.getFullYear(), Math.floor(this.rangeStart.getMonth() / 3) * 3, 1); break;
      case 'year': cursor = new Date(this.rangeStart.getFullYear(), 0, 1); break;
    }
    let guard = 0;
    while (this.daysBetween(this.rangeStart, cursor) < rangeEndExclusive && guard < 2000) {
      guard++;
      let next: Date;
      switch (unit) {
        case 'day': next = this.addDays(cursor, 1); break;
        case 'week': next = this.addDays(cursor, 7); break;
        case 'month': next = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1); break;
        case 'quarter': next = new Date(cursor.getFullYear(), cursor.getMonth() + 3, 1); break;
        case 'year': next = new Date(cursor.getFullYear() + 1, 0, 1); break;
      }
      const startDays = Math.min(Math.max(this.daysBetween(this.rangeStart, cursor), 0), rangeEndExclusive);
      const endDays = Math.min(Math.max(this.daysBetween(this.rangeStart, next), 0), rangeEndExclusive);
      const left = startDays * this.pxPerDay;
      const width = (endDays - startDays) * this.pxPerDay;
      if (width > 0) segs.push({ label: label(cursor), left, width, key: `${unit}-${cursor.getTime()}` });
      cursor = next;
    }
    return segs;
  }

  private static MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // ── Scrolling ─────────────────────────────────────────────────────────────────
  onTimeScroll(): void {
    if (this.scrollSyncing) { this.scrollSyncing = false; return; }
    const t = this.timeScroll?.nativeElement, tree = this.treeScroll?.nativeElement;
    if (t && tree && tree.scrollTop !== t.scrollTop) { this.scrollSyncing = true; tree.scrollTop = t.scrollTop; }
  }

  onTreeScroll(): void {
    if (this.scrollSyncing) { this.scrollSyncing = false; return; }
    const t = this.timeScroll?.nativeElement, tree = this.treeScroll?.nativeElement;
    if (t && tree && t.scrollTop !== tree.scrollTop) { this.scrollSyncing = true; t.scrollTop = tree.scrollTop; }
  }

  scrollToToday(): void {
    setTimeout(() => {
      const el = this.timeScroll?.nativeElement;
      if (!el || !this.todayVisible) return;
      el.scrollLeft = Math.max(0, this.todayLeft - el.clientWidth / 2);
    });
  }

  // ── Drag to reschedule ──────────────────────────────────────────────────────
  startDrag(item: GanttItem, mode: DragState['mode'], ev: PointerEvent): void {
    if (item.isProject) return; // project bars are computed from children
    ev.preventDefault();
    ev.stopPropagation();
    this.dragState = {
      item, mode,
      startClientX: ev.clientX,
      origStart: new Date(item.start),
      origEnd: new Date(item.end),
      previewStart: new Date(item.start),
      previewEnd: new Date(item.end),
      moved: false,
    };
  }

  @HostListener('document:pointermove', ['$event'])
  onPointerMove(ev: PointerEvent): void {
    const ds = this.dragState;
    if (!ds) return;
    const deltaDays = Math.round((ev.clientX - ds.startClientX) / this.pxPerDay);
    if (deltaDays !== 0) ds.moved = true;
    let s = new Date(ds.origStart);
    let e = new Date(ds.origEnd);
    if (ds.mode === 'move') { s = this.addDays(s, deltaDays); e = this.addDays(e, deltaDays); }
    else if (ds.mode === 'resize-start') { s = this.addDays(s, deltaDays); if (s > e) s = new Date(e); }
    else if (ds.mode === 'resize-end') { e = this.addDays(e, deltaDays); if (e < s) e = new Date(s); }
    ds.previewStart = s;
    ds.previewEnd = e;
  }

  @HostListener('document:pointerup')
  onPointerUp(): void {
    const ds = this.dragState;
    if (!ds) return;
    this.dragState = null;
    if (!ds.moved) {
      // Treated as a click: open detail for tasks
      if (!ds.item.isStage && !ds.item.isProject) this.openDetail(ds.item);
      return;
    }
    ds.item.start = ds.previewStart;
    ds.item.end = ds.previewEnd;
    this.commitDates(ds.item);
    this.lastTaskSig = this.computeTaskSig();
    this.rebuild(); // dates changed → range/geometry may shift
  }

  private commitDates(item: GanttItem): void {
    if (this.isTaskLocked(item)) return;
    if (item.isStage) {
      this.stageChange.emit({
        stageId: item.realId,
        changes: { startDate: this.toDateInputValue(item.start), endDate: this.toDateInputValue(item.end) },
      });
    } else if (!item.isProject) {
      this.taskService.updateTask(item.realId, {
        startDate: new Date(this.toDateInputValue(item.start) + 'T12:00:00').toISOString(),
        limitDateToDo: new Date(this.toDateInputValue(item.end) + 'T12:00:00').toISOString(),
      }).catch(err => console.error('Error updating task dates', err));
    }
  }

  // ── Inline editing ──────────────────────────────────────────────────────────
  startEdit(item: GanttItem, field: 'name' | 'hours', ev?: Event): void {
    ev?.stopPropagation();
    if (item.isProject && field === 'name') { return; }
    if (this.isTaskLocked(item)) { return; }
    this.editing = { id: item.id, field };
  }

  /** A completed task is frozen — only its status dropdown stays live, to reopen it. */
  isTaskLocked(item: GanttItem): boolean {
    return !item.isProject && !item.isStage && item.status === 'DONE';
  }

  isEditing(item: GanttItem, field: 'name' | 'hours'): boolean {
    return this.editing?.id === item.id && this.editing?.field === field;
  }

  commitName(item: GanttItem, value: string): void {
    this.editing = null;
    const v = (value || '').trim();
    if (!v || v === item.title) return;
    item.title = v;
    if (item.isProject) this.projectChange.emit({ projectId: item.realId, changes: { projectName: v } });
    else if (item.isStage) this.stageChange.emit({ stageId: item.realId, changes: { stageName: v } });
    else this.taskService.updateTask(item.realId, { nameTask: v }).catch(err => console.error('Error updating task title', err));
  }

  commitHours(item: GanttItem, value: string): void {
    this.editing = null;
    const n = parseFloat(String(value).replace(',', '.'));
    const h = isNaN(n) ? 0 : Math.max(0, n);
    if (h === (item.estimatedHours || 0)) return;
    item.estimatedHours = h;
    if (item.isProject) this.projectChange.emit({ projectId: item.realId, changes: { estimatedHours: h } });
    else if (item.isStage) this.stageChange.emit({ stageId: item.realId, changes: { estimatedHours: h } });
    else this.taskService.updateTask(item.realId, { estimatedHours: h }).catch(err => console.error('Error updating task estimate', err));
  }

  onDateChange(item: GanttItem, field: 'start' | 'end', value: string): void {
    if (!value || item.isProject || this.isTaskLocked(item)) return;
    const dateObject = new Date(value + 'T12:00:00');
    item[field] = dateObject;
    if (item.isStage) {
      this.stageChange.emit({ stageId: item.realId, changes: { [field === 'start' ? 'startDate' : 'endDate']: value } });
    } else {
      this.taskService.updateTask(item.realId, { [field === 'start' ? 'startDate' : 'limitDateToDo']: dateObject.toISOString() })
        .catch(err => console.error('Error updating task date', err));
    }
    this.lastTaskSig = this.computeTaskSig();
    this.rebuild(); // range/geometry may shift
  }

  onStatusChange(item: GanttItem, newStatus: string): void {
    if (item.isProject) return;
    const status = newStatus as 'NOTBEGIN' | 'INPROGRESS' | 'DONE';
    if (item.isStage) {
      this.stageChange.emit({ stageId: item.realId, changes: { status } });
      item.status = status;
      item.completionRatio = status === 'DONE' ? 1 : status === 'INPROGRESS' ? 0.5 : 0;
    } else {
      this.taskService.updateTask(item.realId, { status })
        .then(() => {
          item.status = status;
          item.completionRatio = status === 'DONE' ? 1 : status === 'INPROGRESS' ? 0.5 : 0;
        })
        .catch(err => console.error('Error updating task status', err));
    }
  }

  onColorChange(item: GanttItem, newColor: string): void {
    if (item.isProject) {
      item.color = newColor;
      this.projectChange.emit({ projectId: item.realId, changes: { color: newColor } });
    } else if (item.isStage) {
      item.color = newColor;
      this.stageChange.emit({ stageId: item.realId, changes: { color: newColor } });
      // Propagate to child task bars for immediate visual feedback
      (item.subtasks || []).forEach((t: any) => { if (t.parent) t.parent.color = newColor; });
    }
  }

  // ── Add / open / delete ───────────────────────────────────────────────────────
  onAddTaskClick(item: GanttItem, ev?: Event): void {
    ev?.stopPropagation();
    if (item.isStage) this.addTask.emit(item.realId);
  }

  onAddStageClick(item: GanttItem, ev?: Event): void {
    ev?.stopPropagation();
    if (item.isProject) this.addStage.emit(item.realId);
  }

  openDetail(item: GanttItem): void {
    if (item.isStage || item.isProject) return;
    this.taskDetailOpen.emit(item.realId);
  }

  requestDelete(item: GanttItem, ev?: Event): void {
    ev?.stopPropagation();
    if (item.isStage || item.isProject) return;
    this.taskToDelete = this.allTasks.find(t => t.id === item.realId) ?? null;
    this.showConfirmation = true;
  }

  handleConfirm(result: 'ok' | 'cancel'): void {
    this.showConfirmation = false;
    if (result === 'ok' && this.taskToDelete?.id) {
      this.taskService.deleteTask(this.taskToDelete.id)
        .then(() => { this.taskToDelete = null; })
        .catch(err => console.error(err));
    } else this.taskToDelete = null;
  }

  // ── Presentation helpers ──────────────────────────────────────────────────────
  getColorFromStage(item: GanttItem): string {
    if (item.isProject && item.color) return item.color;
    if (item.isStage && item.color) return item.color;
    if (item.parent?.color) return item.parent.color;
    return '#94a3b8';
  }

  getRowTint(item: GanttItem): string {
    const base = this.getColorFromStage(item);
    if (base.startsWith('#') && base.length >= 7) {
      const r = parseInt(base.slice(1, 3), 16);
      const g = parseInt(base.slice(3, 5), 16);
      const b = parseInt(base.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, 0.06)`;
    }
    return 'transparent';
  }

  statusLabel(status: string): string {
    return this.statusOptions.find(o => o.value === status)?.text ?? 'New';
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'INPROGRESS': return 'text-blue-700 bg-blue-50 border-blue-200';
      case 'DONE': return 'text-emerald-700 bg-emerald-50 border-emerald-200';
      default: return 'text-slate-600 bg-slate-50 border-slate-200';
    }
  }

  getStatusDot(status: string): string {
    switch (status) {
      case 'INPROGRESS': return 'bg-blue-500';
      case 'DONE': return 'bg-emerald-500';
      default: return 'bg-slate-400';
    }
  }

  getEncodedHours(item: GanttItem): number {
    if (item.isStage) return item.encodedHours || 0;
    if (item.isProject) return (item.subtasks || []).reduce((acc: number, s: any) => acc + (s.encodedHours || 0), 0);
    return 0;
  }

  private toDateInputValue(date: Date): string {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const month = ('0' + (d.getMonth() + 1)).slice(-2);
    const day = ('0' + d.getDate()).slice(-2);
    return `${d.getFullYear()}-${month}-${day}`;
  }

  toInput(date: Date): string { return this.toDateInputValue(date); }

  trackRow = (_: number, row: Row) => row.item.id;
}
